/**
 * Lớp gọi API dùng chung.
 *
 * Ba việc bắt buộc phải làm đúng:
 *  1. Gắn Bearer access token vào mọi request.
 *  2. Gặp 401 thì tự đổi token mới bằng refreshToken RỒI GỌI LẠI đúng một lần.
 *     Nhiều request 401 cùng lúc chỉ được đổi token MỘT lần (single-flight),
 *     nếu không server sẽ nhận một loạt lệnh refresh trùng nhau.
 *  3. Ném lỗi CÓ KÈM body JSON. Màn hình lịch đóng cắt cần đọc mảng `losses`
 *     trong body của HTTP 409 — nuốt mất body là hỏng nghiệp vụ.
 */

import type { LoginResponse, AuthUser } from './types';
import { ghiNhanGioTuPhanHoi } from './giomaychu';

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '');

const KEY_ACCESS = 'sdkd.accessToken';
const KEY_REFRESH = 'sdkd.refreshToken';
const KEY_USER = 'sdkd.user';

/** Sự kiện phát ra khi phiên hỏng hẳn — AuthProvider bắt để đá về màn đăng nhập. */
export const EVENT_SESSION_LOST = 'sdkd:session-lost';
/** Phiên vẫn sống nhưng QUYỀN vừa bị hạ — khác hẳn mất phiên, nên là một sự kiện khác. */
export const EVENT_ROI_HANG = 'sdkd:roi-hang';

/**
 * 🔴 TÀI KHOẢN BỊ CHẶN THEO HẠN (`P-6` `A.4`) — khác hẳn `EVENT_SESSION_LOST` và khác hẳn
 * `EVENT_ROI_HANG`, nên nó là sự kiện thứ BA chứ không phải một cờ thêm vào một trong hai:
 *     mất phiên  ⇒ đăng nhập lại là xong
 *     rơi hạng   ⇒ phiên VẪN SỐNG, quyền thấp đi
 *     bị chặn    ⇒ phiên chết, và đăng nhập lại KHÔNG cứu được — phải có người gia hạn
 * Gộp ca thứ ba vào ca thứ nhất là bắt người dùng gõ lại mật khẩu để nhận cùng một lỗi.
 */
export const EVENT_BI_CHAN = 'sdkd:bi-chan';
/**
 * Tủ vừa TRẢ LỜI một lệnh — kèm `detail` mang giá trị bị từ chối. Màn hình nào vừa gửi
 * lệnh thì nghe sự kiện này; làm mới bộ đệm KHÔNG đưa câu ấy tới mắt người bấm nút.
 */
export const EVENT_LENH_ACK = 'sdkd:lenh-ack';
/**
 * MỘT cảnh báo vừa được DỰNG hoặc vừa ĐỔI MỨC (YC-29 PHẦN 2).
 *
 * ⛔ Cố ý là một sự kiện riêng, KHÔNG dùng chung đường làm-mới-bộ-đệm: làm mới bộ đệm chỉ
 *   khiến một con số trong danh sách nhích lên — nó không đưa được gì tới **mắt người đang
 *   trực**, mà mức 1 thì đúng là phải tới mắt. Cùng lý do đã tách `EVENT_LENH_ACK`.
 */
export const EVENT_CANH_BAO_MOI = 'sdkd:canh-bao-moi';

/**
 * MỘT cảnh báo vừa được ĐÓNG vì **tủ báo đã hết** (YC-47).
 *
 * ⛔ Đối xứng với `EVENT_CANH_BAO_MOI`, và nó phải tồn tại vì cùng một lý do: làm mới bộ đệm
 *   chỉ sửa một danh sách: nó **không với tới cái popup đang che màn hình**. Mở tới mắt người
 *   thì đóng cũng phải tới mắt người.
 * ⚠ Đây KHÔNG phải "người trực đã xác nhận" — chuyện ấy là `xacNhan()`, và nó vào nhật ký.
 *   Sự kiện này nói *"nguyên nhân đã hết"*, một câu của TỦ.
 */
export const EVENT_CANH_BAO_DONG = 'sdkd:canh-bao-dong';

/** Hình dạng `detail` của `EVENT_CANH_BAO_DONG` — do `dongCanhBao()` phía máy chủ phát. */
export interface CanhBaoDong {
  cabinetId: string;
  codes?: string[];
  soHang?: number;
  luc?: number;
}

/** Hình dạng `detail` của `EVENT_CANH_BAO_MOI` — do `moCanhBao()` phía máy chủ phát. */
export interface CanhBaoMoi {
  id?: string;
  cabinetId: string;
  code?: string;
  message?: string | null;
  /** ⛔ `null` = không có mức riêng ⇒ tra `BANG_MUC` theo mã. KHÔNG phải mức 2. */
  mucDo?: 1 | 2 | null;
  viec?: 'moi' | 'nang-muc';
  luc?: number;
}

/* ----------------------------------------------------------- kho token */

export const tokenStore = {
  get access(): string | null {
    try { return localStorage.getItem(KEY_ACCESS); } catch { return null; }
  },
  get refresh(): string | null {
    try { return localStorage.getItem(KEY_REFRESH); } catch { return null; }
  },
  get user(): AuthUser | null {
    try {
      const raw = localStorage.getItem(KEY_USER);
      return raw ? (JSON.parse(raw) as AuthUser) : null;
    } catch { return null; }
  },
  save(data: { accessToken: string; refreshToken?: string; user?: AuthUser }) {
    try {
      localStorage.setItem(KEY_ACCESS, data.accessToken);
      if (data.refreshToken) localStorage.setItem(KEY_REFRESH, data.refreshToken);
      if (data.user) localStorage.setItem(KEY_USER, JSON.stringify(data.user));
    } catch { /* trình duyệt chặn localStorage — vẫn chạy được trong phiên hiện tại */ }
  },
  saveUser(user: AuthUser) {
    try { localStorage.setItem(KEY_USER, JSON.stringify(user)); } catch { /* noop */ }
  },
  clear() {
    try {
      localStorage.removeItem(KEY_ACCESS);
      localStorage.removeItem(KEY_REFRESH);
      localStorage.removeItem(KEY_USER);
    } catch { /* noop */ }
  },
};

/* --------------------------------------------------------------- lỗi API */

export class ApiError extends Error {
  readonly status: number;
  /** Body JSON server trả về — chứa `losses`, `detail`, `hint`… */
  readonly body: any;

  constructor(status: number, body: any, fallback: string) {
    super(
      (body && typeof body === 'object' && typeof body.error === 'string' ? body.error : null) ??
      fallback
    );
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  /** Danh sách mất mát khi hạ giao thức v1 (chỉ có ở HTTP 409 của màn hình lịch). */
  get losses(): string[] {
    return Array.isArray(this.body?.losses) ? (this.body.losses as string[]) : [];
  }

  get hint(): string | null {
    return typeof this.body?.hint === 'string' ? this.body.hint : null;
  }
}

/** Lấy câu mô tả lỗi tiếng Việt từ bất kỳ thứ gì bị ném ra. */
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Lỗi không xác định';
}

/* -------------------------------------------------- đổi token (1 lần thôi) */

let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshing) return refreshing;

  refreshing = (async () => {
    const rt = tokenStore.refresh;
    if (!rt) return null;
    try {
      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: rt }),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as {
        accessToken: string;
        /** Máy chủ báo phiên vừa bị HẠ QUYỀN vì vai trong CSDL không đọc được. */
        roiHang?: { vaiCu: string; vaiMoi: string; loi: string };
      };
      if (!data?.accessToken) return null;
      tokenStore.save({ accessToken: data.accessToken });
      /*
       * 🔴 ĐẦU THỨ BA của việc "rơi hạng phải ra tiếng". Máy chủ đã ghi log và đã gắn cờ;
       *   nếu web nuốt cờ ấy thì cả cơ chế bằng KHÔNG với đúng người cần biết — người đang
       *   trực ca, đang bấm nút và thấy nút không ăn. Họ sẽ báo *"app hỏng"*, và người đi
       *   sửa sẽ đi tìm lỗi giao diện ở một chỗ không hỏng.
       */
      if (data.roiHang) {
        window.dispatchEvent(new CustomEvent(EVENT_ROI_HANG, { detail: data.roiHang }));
      }
      return data.accessToken;
    } catch {
      return null;
    } finally {
      // để lần 401 kế tiếp (sau khi token mới cũng hết hạn) còn thử lại được
      setTimeout(() => { refreshing = null; }, 0);
    }
  })();

  return refreshing;
}

/**
 * Xin access token mới ngay lập tức. Dùng cho kênh WebSocket: server chỉ kiểm tra
 * token đúng một lần lúc bắt tay, nên khi bị đá ra với mã 4401 thì phải đổi token
 * trước khi nối lại — nếu không sẽ nối lại vô hạn bằng đúng cái token đã hết hạn.
 */
export function forceRefreshToken(): Promise<string | null> {
  return refreshAccessToken();
}

function loseSession() {
  tokenStore.clear();
  window.dispatchEvent(new CustomEvent(EVENT_SESSION_LOST));
}

/* ---------------------------------------------------------------- request */

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Không gắn token (dùng cho /api/auth/login) */
  anonymous?: boolean;
  signal?: AbortSignal;
}

async function parseBody(res: Response): Promise<any> {
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return text; }
}

async function request<T>(path: string, opts: RequestOptions = {}, isRetry = false): Promise<T> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  if (!opts.anonymous) {
    const token = tokenStore.access;
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  } catch (e) {
    throw new ApiError(0, null, 'Không kết nối được máy chủ — kiểm tra đường truyền');
  }

  // Lệch giờ máy chủ ↔ máy này, lấy từ header `Date`. Đặt ở ĐÂY chứ không ở nhánh `res.ok`:
  // một phản hồi 4xx vẫn mang giờ máy chủ đúng như phản hồi 200. Xem `giomaychu.ts`.
  ghiNhanGioTuPhanHoi(res);

  // Hết hạn token: đổi token rồi gọi lại đúng một lần.
  if (res.status === 401 && !opts.anonymous && !isRetry) {
    const fresh = await refreshAccessToken();
    if (fresh) return request<T>(path, opts, true);
    loseSession();
    throw new ApiError(401, await parseBody(res), 'Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại');
  }

  const body = await parseBody(res);

  if (!res.ok) {
    if (res.status === 401 && !opts.anonymous) loseSession();
    /*
     * ═══════════════════════════════════════════════════════════════════════════════════
     * 🔴 `P-6` `B4` mục 4 — TÀI KHOẢN BỊ CHẶN THEO HẠN: **403 có `maChan`**, không phải 401.
     *
     * Máy chủ phân biệt hai chuyện, và trình duyệt phải giữ nguyên sự phân biệt ấy:
     *     401  phiên hỏng / hết hạn TOKEN  ⇒ đăng nhập lại là xong
     *     403 + maChan  TÀI KHOẢN hết hạn / bị khoá / cấp trên hết hạn ⇒ đăng nhập lại
     *                   **KHÔNG xong**, và người dùng phải biết vì sao
     * ⛔ Đá về màn đăng nhập mà không nói gì là đẩy người ta vào một vòng lặp: gõ lại đúng mật
     *   khẩu, lại bị đá ra, rồi gọi điện bảo *"app hỏng"*. Nên: phát một sự kiện MANG THEO câu
     *   của máy chủ, và `AuthProvider` vẽ nó ra rồi mới đưa về đăng nhập.
     * ⚠ Chỉ nhận khi thân có ĐÚNG trường `maChan` — không đoán theo mã 403 trần, vì 403 còn
     *   dùng cho "thiếu tick" và "không có cờ tuyến", hai ca KHÔNG được đá ai ra khỏi phiên.
     * ═══════════════════════════════════════════════════════════════════════════════════
     */
    if (res.status === 403 && body && typeof body === 'object' && typeof body.maChan === 'string') {
      window.dispatchEvent(new CustomEvent(EVENT_BI_CHAN, {
        detail: { ma: body.maChan, cau: String(body.error ?? ''), ten: body.tenLienQuan ?? null },
      }));
    }
    throw new ApiError(res.status, body, `Máy chủ trả lỗi ${res.status}`);
  }

  return body as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body ?? {} }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body: body ?? {} }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body: body ?? {} }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

/* ------------------------------------------------------------- xác thực */

export async function login(username: string, password: string): Promise<LoginResponse> {
  const data = await request<LoginResponse>('/api/auth/login', {
    method: 'POST',
    body: { username, password },
    anonymous: true,
  });
  tokenStore.save({ accessToken: data.accessToken, refreshToken: data.refreshToken, user: data.user });
  return data;
}

export async function logout(): Promise<void> {
  const rt = tokenStore.refresh;
  try {
    if (rt) await api.post('/api/auth/logout', { refreshToken: rt });
  } catch { /* kể cả server lỗi vẫn phải xoá token phía máy người dùng */ }
  tokenStore.clear();
}

/**
 * TK-18 · NỢ-AT-05 — JWT KHÔNG còn nằm trong URL. URL đi qua access log nginx, lịch sử trình duyệt,
 * Referer; nên token đi trong `Sec-WebSocket-Protocol` (`wsProtocols()`), cái trình duyệt KHÔNG ghi
 * vào đâu cả. Máy chủ echo lại `kapp-jwt` (server/src/services/xacthucws.ts).
 */
export const SUBPROTO_JWT = 'kapp-jwt';
export function wsProtocols(): string[] | null {
  const token = tokenStore.access;
  return token ? [SUBPROTO_JWT, token] : null;
}

export function buildWsUrl(): string | null {
  const token = tokenStore.access;
  if (!token) return null;

  const explicit = import.meta.env.VITE_WS_URL as string | undefined;
  if (explicit) return explicit;   // TK-18: không còn ?token=

  // Không khai báo thì suy ra từ trang hiện tại (dev: Vite proxy /ws sang :8080)
  const base = API_BASE || window.location.origin;
  const url = new URL(base);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.pathname = '/ws/app';
  url.search = '';   // TK-18: token đi bằng subprotocol, xem wsProtocols()
  return url.toString();
}
